/**
 * Non-card funnel beacons (PRD 13 K1 funnel half) → POST /api/funnel/events.
 *
 * The pre/post-cutover funnel comparison: legacy recorded these as fake pageview
 * paths. Mapping (docs/metrics/cutover/legacy-url-coverage.md §Funnel tracking):
 *
 *   /card/send/create                         → composer_open
 *   /card/send/create/{personalize,…}         → composer_step  stage = section
 *   /card/send/create/delivery                → composer_step  stage = "delivery"
 *   /card/send/create/signers-promo | signers → composer_step  stage = "signers-promo" | "signers"
 *   /card/send/create/success                 → send_success
 *   /card/send/create/paywall                 → paywall_shown  surface = "composer"
 *   /card/send/create/{register,login,forgot} → authwall_shown surface = "composer", stage
 *   /mycards/{login,forgot}                   → authwall_shown surface = "mycards", stage
 *   /card/{read,sign/edit,…}/register, /birthday/request/register
 *                                             → authwall_shown surface = that page, stage
 *
 * Every event carries a per-tab `client_session_id` so the numbers can be counted
 * per session (legacy's composer count needs the same de-dup). Fire-and-forget.
 */

export type FunnelEventName =
  | "composer_open"
  | "composer_step"
  | "paywall_shown"
  | "send_success"
  | "authwall_shown"
  /** A received card's public share link went out (detail = channel). */
  | "card_shared"
  /** The share view's "Send a card" call to action was clicked. */
  | "share_cta"
  // ── Measurement P1 (BDAY-194) — the paywall funnel's two inner steps ──
  /** The payment fields mounted and are usable (see `usePaywallFunnel`). */
  | "checkout_opened"
  /** The paywall closed without a payment going through. */
  | "paywall_dismissed"
  /**
   * The composer banner's benefits screen opened — the step BEFORE the plan picker, and
   * the only paywall entry with one. Separate from `paywall_shown` (which stays "prices
   * shown", comparable across surfaces); benefits minus shown is the bail-at-benefits.
   */
  | "paywall_benefits_shown";

/**
 * Where a paywall opened — the `surface` on `paywall_shown` / `checkout_opened` /
 * `paywall_dismissed` (Measurement P1, BDAY-194). The server keeps the same list in
 * `FUNNEL_SURFACES` (server/routes/public/funnel-events.ts) and NULLS anything outside
 * it, so a value added here has to be added there too or it silently stops being
 * attributed. `composer` predates P1 and is deliberately unchanged: it is the series the
 * legacy baseline (`/card/send/create/paywall`) compares against.
 */
export type PaywallSurface =
  /** The delivery step's 402 — legacy's `/card/send/create/paywall`. */
  | "composer"
  /** The composer's premium-card band upsell ("View premium plans"). */
  | "composer_banner"
  /** A gift flow needing premium first (promo CTA, or a 402 from the picker). */
  | "composer_gift"
  /** The message dialog's Generate Message (AI suggestions are premium — Nick, 2026-10-05). */
  | "composer_message"
  /** The marketing homepage's premium panel. */
  | "landing"
  /** The `/payment` account surface. */
  | "payment"
  /** The billing-recovery email's landing flow. */
  | "billing_recovery"
  /** Inline card recovery for a past-due member (a different offer to the paywall). */
  | "payment_recovery";

export interface FunnelProps {
  surface?: string | null;
  stage?: string | null;
  detail?: string | null;
  card_send_id?: string | null;
}

interface Queued extends FunnelProps {
  event: FunnelEventName;
  client_session_id: string;
  client_request_id: string;
}

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return "00000000-0000-4000-8000-000000000000";
  }
}

let sessionId: string | null = null;
function clientSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = sessionStorage.getItem("bday_funnel_sid");
    if (existing) return (sessionId = existing);
    const fresh = uuid();
    sessionStorage.setItem("bday_funnel_sid", fresh);
    return (sessionId = fresh);
  } catch {
    return (sessionId = uuid());
  }
}

const queue: Queued[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
/** Suppress an identical event re-fired by a re-render within a moment. */
let last: { key: string; at: number } | null = null;

function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const events = queue.splice(0, 50);
  try {
    void fetch("/api/funnel/events", {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch {
    /* never surface */
  }
  if (queue.length) timer = setTimeout(flush, 1500);
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

export function trackFunnel(event: FunnelEventName, props: FunnelProps = {}): void {
  const key = `${event}|${props.surface ?? ""}|${props.stage ?? ""}|${props.card_send_id ?? ""}`;
  const now = Date.now();
  if (last && last.key === key && now - last.at < 1500) return;
  last = { key, at: now };
  queue.push({
    ...props,
    event,
    client_session_id: clientSessionId(),
    client_request_id: uuid(),
  });
  // send_success fires right before a navigation — don't wait for the timer.
  if (event === "send_success" || queue.length >= 50) flush();
  else if (!timer) timer = setTimeout(flush, 1500);
}

/** Which page an auth wall sits over, from the login page's `?next=` (legacy's per-surface path). */
export function authSurfaceFromNext(next: string | null | undefined): string {
  const p = (next ?? "").split("?")[0] ?? "";
  if (p.startsWith("/mycards")) return "mycards";
  if (p.startsWith("/card/read")) return "card_read";
  if (p.startsWith("/card/sign/edit")) return "card_sign_edit";
  if (p.startsWith("/card/sign")) return "card_sign_view";
  if (p.startsWith("/card/view")) return "card_view";
  if (p.startsWith("/card/send")) return "composer";
  // Birthday Requests: /dob/:token (new) — the legacy /bday|/bd5–7 magic links redirect there.
  if (p.startsWith("/dob/") || p.startsWith("/birthday/request")) return "birthday_request";
  return "direct";
}
